// Google integration coordinator. The banner UI never calls into this module; the bootstrap does.
import { createGtag, getDataLayer, toConsentState } from './google-consent-mode.js';
import { GTAG_SRC, loadGtag } from './gtag.js';
import { GTM_SRC, loadGtmContainers } from './gtm.js';
import { isObject, safeUrl, warn } from '../utils.js';

/** dataLayer event pushed after every consent change, for GTM triggers. */
export const UPDATE_EVENT = 'consent_banner_update';

const TAG_ID = /^[A-Z]{1,3}-[A-Z0-9-]{4,30}$/;
const GTM_ID = /^GTM-[A-Z0-9]{4,12}$/;
const DATA_LAYER_NAME = /^[A-Za-z_$][\w$]{0,63}$/;

/**
 * Validates `google` config. Invalid entries are skipped with a warning; duplicate GTM IDs load
 * once (first wins). `dataLayers` lists every distinct dataLayer the tags use.
 *
 * `src` is the full URL of the loader script, for first-party setups such as a server-side GTM
 * domain or a Google tag gateway path. Relative URLs resolve against `base`. It defaults to
 * Google's endpoint, and an invalid one skips that tag rather than quietly loading it from Google.
 */
export function normalizeGoogleConfig(input, base) {
  if (!isObject(input)) return null;

  let gtag = null;
  if (isObject(input.gtag)) {
    const dataLayer = dataLayerName(input.gtag.dataLayer);
    const src = scriptSrc(input.gtag.src, GTAG_SRC, base, 'gtag.js');
    const ids = unique([].concat(input.gtag.ids || []).map(toId).filter((id) => valid(TAG_ID, id, 'gtag ID')));
    if (dataLayer && src && ids.length) gtag = { ids, dataLayer, src };
  }

  const gtm = [];
  const containers = isObject(input.gtm) && Array.isArray(input.gtm.containers) ? input.gtm.containers : [];
  for (const container of containers) {
    const id = toId(container && container.id);
    const dataLayer = dataLayerName(container && container.dataLayer);
    if (!valid(GTM_ID, id, 'GTM container ID') || !dataLayer) continue;
    if (gtm.some((loaded) => loaded.id === id)) continue;
    // A container's own `src` wins over the `gtm.src` shared by all containers.
    const src = scriptSrc(container.src === undefined ? input.gtm.src : container.src, GTM_SRC, base, `GTM container ${id}`);
    if (src) gtm.push({ id, dataLayer, src });
  }

  const dataLayers = unique([gtag && gtag.dataLayer, ...gtm.map((container) => container.dataLayer)].filter(Boolean));
  return {
    consentMode: input.consentMode !== false,
    gtag,
    gtm,
    dataLayers: dataLayers.length ? dataLayers : ['dataLayer'],
  };
}

/**
 * Consent state lives in the bootstrap. This mirrors it into every dataLayer in use, so each
 * gtag.js / GTM instance sees the same commands in the same order whichever loads first.
 */
export function createGoogleIntegration(win, config, { nonce } = {}) {
  const gtags = config.dataLayers.map((name) => createGtag(win, name));

  return {
    /** Creates/reuses the dataLayers and queues the GRANTED bootstrap default. */
    initializeGoogleConsent() {
      for (const name of config.dataLayers) {
        if (!getDataLayer(win, name)) warn(`window.${name} exists but is not a dataLayer; leaving it untouched`);
      }
      if (!config.consentMode) return;
      for (const gtag of gtags) gtag('consent', 'default', toConsentState({ analytics: true, marketing: true }));
    },

    /**
     * Queues the effective consent. During bootstrap (`initial`) this only happens when something
     * is denied. Later changes always update and also push `consent_banner_update` for GTM.
     */
    updateGoogleConsent(granted, { initial = false, regime } = {}) {
      const state = toConsentState(granted);
      const anyDenied = Object.values(state).includes('denied');
      if (config.consentMode && (anyDenied || !initial)) {
        for (const gtag of gtags) gtag('consent', 'update', { ...state });
      }
      if (initial) return;
      for (const name of config.dataLayers) {
        const layer = getDataLayer(win, name);
        if (!layer) continue;
        layer.push({
          event: UPDATE_EVENT,
          consent_banner: {
            regime,
            analytics: state.analytics_storage,
            marketing: state.ad_storage,
          },
        });
      }
    },

    /** Loads gtag.js and the GTM containers once. They stay loaded; Consent Mode governs them. */
    loadGoogleTags() {
      if (config.gtag) loadGtag(win, config.gtag, { nonce });
      loadGtmContainers(win, config.gtm, { nonce });
    },
  };
}

function toId(value) {
  return typeof value === 'string' ? value.trim().toUpperCase() : '';
}

function valid(pattern, id, label) {
  if (pattern.test(id)) return true;
  warn(`Ignoring invalid ${label}:`, id);
  return false;
}

function scriptSrc(value, fallback, base, label) {
  if (value === undefined) return fallback;
  const src = safeUrl(value, base);
  if (!src) warn(`Invalid src for ${label}, not loading it:`, value);
  return src;
}

function dataLayerName(value) {
  if (value === undefined) return 'dataLayer';
  if (typeof value === 'string' && DATA_LAYER_NAME.test(value)) return value;
  warn('Ignoring invalid dataLayer name:', value);
  return null;
}

function unique(values) {
  return Array.from(new Set(values));
}
